import { useState } from 'react';

import RefreshIcon from '@mui/icons-material/Refresh';
import PropTypes from 'prop-types';

import Button from '@zenbusiness/zui/Button';

import { CLICKSTREAM_COMMON_PROPS } from '#client/components/utils/constants';

import IdeaCard from './IdeaCard';
import * as Styled from '../../BusinessResults.style';

/**
 * Left sidebar panel containing list of business ideas
 * @param {object} props - Component props
 * @param {Array} props.ideas - Array of business ideas
 * @param {string} props.selectedIdeaId - ID of currently selected idea
 * @param {Function} props.onIdeaSelect - Handler for idea selection
 * @param {Function} props.onGenerateMore - Handler for generating more ideas
 * @param {Function} props.onStartOver - Handler for starting over
 * @param {boolean} [props.loading=false] - Whether more ideas are being generated
 * @component
 */
function IdeasPanel({
  ideas,
  selectedIdeaId,
  onIdeaSelect,
  onGenerateMore,
  onStartOver,
  loading = false
}) {
  const [generateLoading, setGenerateLoading] = useState(false);
  const [startOverLoading, setStartOverLoading] = useState(false);

  const handleGenerateMore = async () => {
    if (generateLoading || startOverLoading || loading) return;

    setGenerateLoading(true);
    if (onGenerateMore && !loading) {
      await onGenerateMore();
    }
    setGenerateLoading(false);
  };

  const handleStartOver = async () => {
    if (startOverLoading || generateLoading || loading) return;

    setStartOverLoading(true);
    if (onStartOver && !loading) {
      await onStartOver();
    }
    setStartOverLoading(false);
  };

  return (
    <Styled.IdeasSidebar>
      <Styled.IdeasList>
        {ideas.map((idea) => (
          <IdeaCard
            key={idea.id}
            idea={idea}
            selected={idea.id === selectedIdeaId}
            onClick={onIdeaSelect}
          />
        ))}
      </Styled.IdeasList>

      <Styled.ButtonsContainer>
        <Button
          clickstreamProps={{
            ...CLICKSTREAM_COMMON_PROPS,
            elementName: 'business_idea_generate_more',
            elementText: 'Generate more ideas'
          }}
          color="info"
          disabled={generateLoading || startOverLoading || loading}
          fullWidth
          loading={generateLoading}
          size="medium"
          startIcon={<RefreshIcon />}
          variant="outlined"
          onClick={handleGenerateMore}
        >
          Generate more ideas
        </Button>
        <Button
          clickstreamProps={{
            ...CLICKSTREAM_COMMON_PROPS,
            elementName: 'business_idea_start_over',
            elementText: 'Start over'
          }}
          disabled={generateLoading || startOverLoading || loading}
          fullWidth
          loading={startOverLoading}
          size="medium"
          variant="outlined"
          // startIcon={<RefreshIcon />}
          onClick={handleStartOver}
        >
          Start over
        </Button>
      </Styled.ButtonsContainer>
    </Styled.IdeasSidebar>
  );
}

IdeasPanel.propTypes = {
  ideas: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string // Backward compatibility
    })
  ).isRequired,
  selectedIdeaId: PropTypes.string,
  onIdeaSelect: PropTypes.func,
  onGenerateMore: PropTypes.func,
  onStartOver: PropTypes.func,
  loading: PropTypes.bool
};

export default IdeasPanel;
